<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import { authApi } from "../lib/api";
  import { authStore, addNotification } from "../lib/stores";
  import Toast from "../components/overlay/Toast.svelte";
  import Button from "../components/input/Button.svelte";

  let email = $state("");
  let password = $state("");
  let rememberMe = $state(false);
  let loading = $state(false);
  let showPassword = $state(false);
  let errors = $state<Record<string, string>>({});
  let showForgotPasswordModal = $state(false);
  let forgotPasswordEmail = $state("");
  let sendingResetEmail = $state(false);
  let resetEmailSent = $state(false);
  const testAccounts = [
    { email: "test1@triphelper.com", label: "김여행", accent: "blue" },
    { email: "test2@triphelper.com", label: "이동이", accent: "green" },
    { email: "test3@triphelper.com", label: "박친구", accent: "amber" },
  ] as const;

  function handleSocialLogin(provider: string) {
    addNotification("info", `${provider} 로그인은 현재 준비 중입니다. 곧 지원될 예정입니다!`);
  }

  async function handleForgotPassword() {
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes("@")) {
      addNotification("error", "올바른 이메일 주소를 입력해주세요.");
      return;
    }
    
    sendingResetEmail = true;
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    resetEmailSent = true;
    sendingResetEmail = false;
  }

  function closeForgotPasswordModal() {
    showForgotPasswordModal = false;
    forgotPasswordEmail = "";
    resetEmailSent = false;
  }

  async function handleSubmit() {
    loading = true;
    errors = {};

    try {
      const response = await authApi.login(email, password);

      if (response.success) {
        try {
          localStorage.setItem("auth_token", response.data.tokens.access_token);
        } catch (e) {
          console.warn("Failed to save auth token to localStorage", e);
        }
        authStore.login(response.data.user);
        addNotification("success", "로그인되었습니다!");
        router.visit("/trips");
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          if (err.field) {
            errors[err.field] = err.message;
          } else {
            errors.general = err.message;
          }
        });
        if (errors.general) {
          addNotification("error", errors.general);
        }
      } else {
        addNotification("error", "로그인에 실패했습니다. 다시 시도해주세요.");
      }
    } finally {
      loading = false;
    }
  }

  // Test account quick login (remove in production)
  async function quickLogin(testEmail: string) {
    loading = true;
    errors = {};

    try {
      const response = await authApi.login(testEmail, "Test@1234");

      if (response.success) {
        try {
          localStorage.setItem("auth_token", response.data.tokens.access_token);
        } catch (e) {
          console.warn("Failed to save auth token to localStorage", e);
        }
        authStore.login(response.data.user);
        addNotification("success", `${testEmail}로 로그인되었습니다!`);
        router.visit("/trips");
      }
    } catch (error: any) {
      addNotification("error", "테스트 계정 로그인에 실패했습니다.");
    } finally {
      loading = false;
    }
  }
</script>

<div class="min-h-screen flex">
  <!-- Left side - Login form -->
  <div class="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">
      <div class="text-center mb-8">
        <Link href="/" class="inline-block text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
          TripHelper
        </Link>
        <h2 class="mt-6 text-2xl font-semibold text-primary">로그인</h2>
        <p class="mt-2 text-secondary">
          계정이 없으신가요?
          <Link href="/signup" class="text-primary-600 hover:text-primary-500 font-medium">
            회원가입
          </Link>
        </p>
      </div>

      <div class="card">
        <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }} class="space-y-6">
          <div>
            <label for="email" class="label">이메일</label>
            <input
              id="email"
              type="email"
              bind:value={email}
              class="input"
              class:input-error={errors.email}
              placeholder="you@example.com"
              required
              autocomplete="email"
            />
            {#if errors.email}
              <p class="mt-1 text-sm text-error">{errors.email}</p>
            {/if}
          </div>

          <div>
            <label for="password" class="label">비밀번호</label>
            <div class="relative">
              {#if showPassword}
                <input
                  id="password"
                  type="text"
                  bind:value={password}
                  class="input pr-10"
                  class:input-error={errors.password}
                  placeholder="••••••••"
                  required
                  autocomplete="current-password"
                />
              {:else}
                <input
                  id="password"
                  type="password"
                  bind:value={password}
                  class="input pr-10"
                  class:input-error={errors.password}
                  placeholder="••••••••"
                  required
                  autocomplete="current-password"
                />
              {/if}
              <button
                type="button"
                onclick={() => (showPassword = !showPassword)}
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-muted hover:text-secondary"
              >
                {#if showPassword}
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                {:else}
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                {/if}
              </button>
            </div>
            {#if errors.password}
              <p class="mt-1 text-sm text-error">{errors.password}</p>
            {/if}
          </div>

          <div class="flex items-center justify-between">
            <label class="flex items-center">
              <input
                type="checkbox"
                bind:checked={rememberMe}
                class="rounded text-primary-600 focus:ring-primary-500"
              />
              <span class="ml-2 text-sm text-secondary">로그인 상태 유지</span>
            </label>
            <button 
              onclick={() => showForgotPasswordModal = true}
              class="text-sm text-primary-600 hover:text-primary-500"
            >
              비밀번호 찾기
            </button>
          </div>

          <Button type="submit" variant="primary" fullWidth loading={loading}>로그인</Button>

          <!-- Test Account Quick Login (Remove in production) -->
          <div class="mt-4 pt-4 border-t border-bright">
            <p class="text-xs text-muted text-center mb-3">테스트 계정 (개발용)</p>
            <p class="text-[11px] text-muted text-center mb-3">공통 비밀번호: Test@1234</p>
            <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {#each testAccounts as account}
                <button
                  type="button"
                  onclick={() => quickLogin(account.email)}
                  disabled={loading}
                  class={`w-full rounded px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                    account.accent === "blue"
                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      : account.accent === "green"
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                  }`}
                >
                  <span class="block">{account.label}</span>
                  <span class="mt-1 block text-[11px] font-normal opacity-80">{account.email}</span>
                </button>
              {/each}
            </div>
          </div>
        </form>

        <!-- Social login -->
        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-bright"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-surface text-muted">또는</span>
            </div>
          </div>

          <div class="mt-6">
            <a
              href="/auth/1pass/initiate"
              rel="external"
              class="w-full flex items-center justify-center px-4 py-2 border border-bright rounded-lg shadow-sm text-sm font-medium text-secondary bg-surface hover:bg-surface-hover transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="4.5" />
                <circle cx="12" cy="12" r="1.4" fill="currentColor" />
              </svg>
              logi 로 로그인
            </a>
          </div>

          <div class="mt-3 grid grid-cols-2 gap-3">
            <button
              type="button"
              onclick={() => handleSocialLogin("Google")}
              class="w-full flex items-center justify-center px-4 py-2 border border-bright rounded-lg shadow-sm text-sm font-medium text-secondary bg-surface hover:bg-surface-hover transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z" />
                <path fill="#34A853" d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z" />
                <path fill="#4A90E2" d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z" />
                <path fill="#FBBC05" d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067Z" />
              </svg>
              Google
            </button>
            <button
              type="button"
              onclick={() => handleSocialLogin("카카오")}
              class="w-full flex items-center justify-center px-4 py-2 border border-bright rounded-lg shadow-sm text-sm font-medium text-secondary bg-surface hover:bg-surface-hover transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#FEE500">
                <path d="M12 3c5.8 0 10.5 3.66 10.5 8.185 0 4.525-4.7 8.185-10.5 8.185a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.867C1.5 6.66 6.2 3 12 3Z" />
              </svg>
              카카오
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Right side - Image/illustration -->
  <div class="hidden lg:flex lg:flex-1 bg-gradient-to-br from-primary-500 to-secondary-600 items-center justify-center p-12">
    <div class="max-w-lg text-white text-center">
      <div class="mb-8">
        <svg class="w-24 h-24 mx-auto opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 class="text-3xl font-bold mb-4">여행을 더 스마트하게</h2>
      <p class="text-lg opacity-90">
        TripHelper와 함께라면 복잡한 여행 경비 관리도 간단해집니다.
        그룹 여행의 정산, 예산 관리까지 한 번에 해결하세요.
      </p>
    </div>
  </div>
</div>

<!-- Forgot Password Modal -->
{#if showForgotPasswordModal}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
  <div 
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
    onclick={closeForgotPasswordModal}
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    aria-labelledby="forgot-password-title"
  >
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions a11y_no_noninteractive_element_interactions -->
    <div 
      class="bg-surface rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl"
      role="document"
    >
      <div class="p-6">
        {#if resetEmailSent}
          <div class="text-center py-4">
            <div class="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-primary mb-2">이메일 전송 완료</h3>
            <p class="text-secondary text-sm mb-6">
              비밀번호 재설정 링크가 <strong>{forgotPasswordEmail}</strong>로 전송되었습니다.
              메일함을 확인해주세요.
            </p>
            <button
              onclick={closeForgotPasswordModal}
              class="w-full py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
            >
              확인
            </button>
          </div>
        {:else}
          <div class="flex items-center gap-3 mb-6">
            <div class="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 class="text-xl font-bold text-primary">비밀번호 찾기</h3>
              <p class="text-muted text-sm">가입한 이메일로 재설정 링크를 보내드립니다</p>
            </div>
          </div>
          
          <form onsubmit={(e) => { e.preventDefault(); handleForgotPassword(); }} class="space-y-4">
            <div>
              <label for="forgot-email" class="label">이메일</label>
              <input
                id="forgot-email"
                type="email"
                bind:value={forgotPasswordEmail}
                class="input"
                placeholder="you@example.com"
                required
              />
            </div>
            
            <div class="flex gap-3 pt-2">
              <button
                type="button"
                onclick={closeForgotPasswordModal}
                class="flex-1 py-3 border border-bright text-secondary rounded-xl font-medium hover:bg-surface-hover transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={sendingResetEmail}
                class="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {sendingResetEmail ? "전송 중..." : "재설정 링크 보내기"}
              </button>
            </div>
          </form>
        {/if}
      </div>
    </div>
  </div>
{/if}

<Toast />
